import { SetStateAction, useEffect, useState } from "react";
import List from "../../components/List/List";
import {
  getApplications,
  getApplicationsCount,
} from "../../api/applicationApi";
import ForwardButton from "../../components/ForwardButton/ForwardButton";
import {
  COLUMN_TYPE,
  DEFAULT_PAGE_SIZE,
  FILTER_ACTION_TYPE,
  FILTER_INPUT_TYPE,
} from "../../constants";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Loader from "../Loader/Loader";
import { SortProperty } from "../SortingSelector/SortingSelector";

export interface ApplicationFilter {
  name?: string;
  packageName?: string;
  platformList?: string;
}

export default function ApplicationList() {
  const [applications, setApplications] = useState<any>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [numberOfApplications, setNumberOfApplications] = useState(-1);
  const [currentSort, setCurrentSort] = useState<SortProperty>();
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [filter, setFilter] = useState<ApplicationFilter | null>(null);
  const [filterPropertyName, setFilterPropertyName] = useState("");
  const [filterPropertyPackageName, setFilterPropertyPackageName] =
    useState("");
  const [filterPropertyPlatformList, setFilterPropertyPlatformList] =
    useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setApplications(null);

    getApplications(
      currentPage * pageSize,
      pageSize,
      sortField,
      sortOrder,
      filter && filter.name ? filter.name : "",
      filter && filter.packageName ? filter.packageName : "",
      filter && filter.platformList ? filter.platformList : "",
      (responseData: SetStateAction<any>) => {
        let applicationsWithAppendedActions: any = [];
        for (let application of responseData) {
          applicationsWithAppendedActions.push(
            Object.assign(
              {
                action: (
                  <ForwardButton linkTo={"/application/" + application.id} />
                ),
              },
              application
            )
          );
        }

        if (applicationsWithAppendedActions.length === 0 && currentPage > 0) {
          setCurrentPage(0);
        } else {
          setApplications(applicationsWithAppendedActions);
        }
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [currentPage, pageSize, sortField, sortOrder, filter]);

  useEffect(() => {
    getApplicationsCount(
      filter && filter.name ? filter.name : "",
      filter && filter.packageName ? filter.packageName : "",
      filter && filter.platformList ? filter.platformList : "",
      (responseData: SetStateAction<any>) => {
        setNumberOfApplications(responseData);
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [filter]);

  const handleChange = (event: any) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    if (name === "name") {
      setFilterPropertyName(value);
    }
    if (name === "packageName") {
      setFilterPropertyPackageName(value);
    }
  };

  const handleFilterChange = (action: string) => {
    if (action === FILTER_ACTION_TYPE.CLEAR) {
      setFilterPropertyName("");
      setFilterPropertyPackageName("");
      setFilterPropertyPlatformList("");
      setFilter({});
    }
    if (action === FILTER_ACTION_TYPE.APPLY) {
      setFilter({
        name: filterPropertyName,
        packageName: filterPropertyPackageName,
        platformList: filterPropertyPlatformList,
      });
    }
  };

  if (error) {
    return <ErrorMessage text={error} />;
  }

  return (
    <>
      {applications && numberOfApplications >= 0 ? (
        <List
          columns={[
            {
              propertyName: "name",
              headerName: "Name",
              type: COLUMN_TYPE.TEXT,
            },
            {
              propertyName: "packageName",
              headerName: "Package",
              type: COLUMN_TYPE.TEXT,
              optional: true,
            },
            {
              propertyName: "createdOn",
              headerName: "Created On",
              type: COLUMN_TYPE.DATE,
              optional: true,
              sortable: true,
            },
            {
              propertyName: "lastModifiedOn",
              headerName: "Last Modified On",
              type: COLUMN_TYPE.DATE,
              optional: true,
              sortable: true,
            },
            {
              propertyName: "action",
              type: COLUMN_TYPE.COMPONENT,
            },
          ]}
          items={applications}
          currentPage={currentPage}
          pageSize={pageSize}
          totalNumberOfItems={numberOfApplications}
          onPageChange={(page: number) => {
            setCurrentPage(page);
          }}
          onPageSizeChange={(pageSize: number) => {
            setCurrentPage(0);
            setPageSize(pageSize);
          }}
          currentSort={currentSort}
          onSortChange={(sortField: string, sortOrder: string) => {
            setCurrentSort({
              propertyName: sortField,
              type: sortOrder,
            });
            setSortField(sortField);
            setSortOrder(sortOrder);
          }}
          filterInputs={[
            {
              type: FILTER_INPUT_TYPE.TEXT,
              propertyName: "name",
              propertyLabel: "Name",
              value: filterPropertyName,
              onChange: handleChange,
            },
            {
              type: FILTER_INPUT_TYPE.TEXT,
              propertyName: "packageName",
              propertyLabel: "Package",
              value: filterPropertyPackageName,
              onChange: handleChange,
            },
            {
              type: FILTER_INPUT_TYPE.MULTI_SELECT,
              propertyName: "platformList",
              propertyLabel: "Platform",
              options: [
                {
                  name: "android",
                  label: "Android",
                  value: "ANDROID",
                },
                {
                  name: "linux",
                  label: "Linux",
                  value: "LINUX",
                },
              ],
              value: filterPropertyPlatformList,
              onChange: setFilterPropertyPlatformList,
            },
          ]}
          onFilterChange={(action: string) => {
            handleFilterChange(action);
          }}
        />
      ) : (
        <Loader />
      )}
    </>
  );
}
